import React from 'react';
import Close from '@monofront/design-system/visuals/Icons/Old/Close';
import Button from '@monofront/design-system/components/Button';
import { openNativeAppDynamicLink } from '@utils/nativeAppLinks';
import { useSessionStorage } from '@monofront/business/hooks';
import { SESSIONSTORAGE_HIDE_MOBILE_APP_SMART_BANNER } from '@constants/sessionStorage';
import { useTranslations } from 'next-intl';
import { isSafari } from '@utils/environment';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';

export interface IBannerAppSmartProps {
  backgroundColor?: string;
  icon?: { props: { src: string; alt: string } };
  iconSize?: number;
  title: string | React.ReactNode;
  text: string | React.ReactNode;
  buttonText: string;
}

const TextWrapper = ({ children, ...props }) => {
  if (typeof children === 'string') {
    return <p {...props}>{children}</p>;
  }

  return <div {...props}>{children}</div>;
};

const BannerAppSmart: React.FC<IBannerAppSmartProps> = ({
  backgroundColor,
  buttonText,
  icon,
  iconSize = 32,
  text,
  title,
}) => {
  const [
    hasClosedBannerForCurrentSession,
    setHasClosedBannerForCurrentSession,
  ] = useSessionStorage<'true' | 'false'>(
    SESSIONSTORAGE_HIDE_MOBILE_APP_SMART_BANNER,
    'false',
  );

  const tGlobal = useTranslations('Global');

  const navigateToStore = () => openNativeAppDynamicLink();

  if (hasClosedBannerForCurrentSession === 'true') {
    return null;
  }

  if (isSafari) {
    return null;
  }

  return (
    <>
      <div className="container">
        <div className="content">
          <button
            className="close-button"
            type="button"
            onClick={() => setHasClosedBannerForCurrentSession('true')}
            aria-label={tGlobal('close')}
          >
            <Close width={10} height={10} />
          </button>
          {icon && (
            <div className="icon">
              <ResponsiveImage
                src={icon.props.src}
                alt={icon.props.alt}
                width={25}
                height={25}
              />
            </div>
          )}
          <div className="text-container">
            <TextWrapper className="title">{title}</TextWrapper>
            <TextWrapper>{text}</TextWrapper>
          </div>
        </div>
        <Button
          block={false}
          hasMinWidth={false}
          className="cta"
          size="medium"
          onClick={navigateToStore}
        >
          {buttonText}
        </Button>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: ${backgroundColor || 'transparent'};
          padding: var(--space-2) var(--space-4);
          gap: var(--space-4);
        }

        .content {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }

        .text-container,
        .text-container :global(*),
        .container :global(.cta) {
          font-size: 12px;
        }

        .text-container :global(.title) {
          font-weight: var(--font-weight-bold);
        }

        .icon {
          min-width: ${iconSize}px;
          min-height: ${iconSize}px;
        }

        .close-button {
          border: none;
          background-color: transparent;
          cursor: pointer;
        }

        .container :global(.cta) {
          padding-left: var(--space-4);
          padding-right: var(--space-4);
        }
      `}</style>
    </>
  );
};

export default BannerAppSmart;
